import { yupResolver } from '@hookform/resolvers/yup'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { Field } from 'src/components/FormComponents/Field'
import { useCustomOnKeyDown } from 'src/hooks/useCustomOnKeyDown'
import { useDockData } from 'src/hooks/useDockData'
import { useReceivement } from 'src/hooks/useReceivement'
import { useRouteToReceivement } from 'src/hooks/useRouteToReceivement'
import { getCurrentReceivement } from 'src/services/docks/dockService'
import {
  getReceivementFromQueue,
  startReceivement
} from 'src/services/receivement/receivementService'
import SwitchMui from 'src/components/SwitchMui/SwitchMui'
import { useDispatch } from 'react-redux'
import { actions as dockActions } from 'src/features/dock-management/DockManagementState'
import * as yup from 'yup'
import * as S from './NewReceivementStart.styles'
import ModalConfirmDockFresh from '../../components/ModalConfirmDockFresh'

const schema = yup.object().shape({
  badge: yup.string().required('Campo obrigatório!'),
  attendancePassword: yup.string().required('Campo obrigatório!')
})

const NewReceivementStart = () => {
  const { routeToReceivement } = useRouteToReceivement()
  const localCurrentDock = useDockData()
  const [isDisabledButton, setIsDisabledButton] = useState(false)
  const { setOperatorBadge, setOperatorName } = useReceivement({
    reset: true
  })
  const { control, handleSubmit, setFocus } = useForm({
    resolver: yupResolver(schema)
  })
  const { handleOnKeyDown } = useCustomOnKeyDown({ setFocus })
  const [fetchedCurrentReceivement, setFetchedCurrentReceivement] =
    useState(false)
  const [currentReceivement, setCurrentReceivement] = useState()
  const [currentDock, setCurrentDock] = useState(localCurrentDock)
  const [showModalConfirmDockFresh, setShowModalConfirmDockFresh] = useState(false)

  const dispatch = useDispatch()

  const updateDockData = (dock) => {
    localStorage.setItem('currentDock', JSON.stringify(dock))
    setCurrentDock(dock)
  }

  const handleCurrentReceivement = useCallback(async () => {
    if (!localCurrentDock) {
      return
    }
  
    const result = await getCurrentReceivement(localCurrentDock?.id)

    if (!result.isOk) {
      toast.error('Falha ao carregar recebimento atual da doca!')
      return
    }

    setFetchedCurrentReceivement(true)
    setCurrentReceivement(result.data.currentReceivement)
  }, [localCurrentDock?.id])

  const changeDockFresh = (badge) => {
    if (!currentDock?.id) {
      return
    }

    if (!showModalConfirmDockFresh) {
      setShowModalConfirmDockFresh(true)
      return
    }

    const onSuccess = () => {
      const dock = {
        ...currentDock,
        is_fresh: !currentDock.is_fresh
      }

      updateDockData(dock)

      setShowModalConfirmDockFresh(false)
    }

    dispatch(dockActions.setDockIsFresh({
      id: currentDock.id,
      isFresh: !currentDock.is_fresh,
      onSuccess,
      onError: () => null,
      badge
    }))
  }

  useEffect(() => {
    if (!localCurrentDock?.id) {
      return
    }

    const onSuccess = (dock) => {
      const dockData = {
        id: dock.id,
        defaultLocationId: dock.defaultLocationId,
        code: dock.code,
        is_fresh: dock.isFresh,
        id_fc: dock.fulfillmentCenterId,
      }

      updateDockData(dockData)
    }

    dispatch(dockActions.getDock({
      id: localCurrentDock.id,
      onSuccess
    }))
  }, [localCurrentDock?.id])

  useEffect(() => {
    handleCurrentReceivement()
  }, [handleCurrentReceivement])

  const callNewReceivement = async () => {
    setIsDisabledButton(true)
    const result = await getReceivementFromQueue(currentDock?.id)
    if (result.isOk) {
      setCurrentReceivement(result.data)
      setIsDisabledButton(false)
      return
    }

    if (result.data.status === 'EMPTY_QUEUE') {
      toast.error('Não há fornecedores disponíveis na fila!')
      setIsDisabledButton(false)
      return
    }

    toast.error('Falha ao carregar um fornecedor da fila!')
    setIsDisabledButton(false)
  }

  const beginReceivement = async ({ badge, attendancePassword }) => {
    const result = await startReceivement(
      badge,
      currentReceivement.id,
      attendancePassword
    )

    if (!result.isOk && result?.data?.error_code !== 'ALREADY_STARTED') {
      toast.error(result.data.message)
      return
    }

    setOperatorBadge(badge)
    setOperatorName(result.data.user)

    routeToReceivement({
      receivementId: currentReceivement.id,
      receivementStatus: result.data.status,
      id_user_release: currentReceivement.id_user_release
    })
  }

  if (!fetchedCurrentReceivement) {
    return (
      <S.Wrapper>
        <S.Title>Carregando...</S.Title>
      </S.Wrapper>
    )
  }

  if (fetchedCurrentReceivement && !currentReceivement) {
    return (
      <>
        <S.Wrapper>
          <S.Card>
            <h2>
              Doca {currentDock?.code} - {currentDock?.is_fresh ? 'Fresh' : 'Mercearia'}
            </h2>
            <S.Title>Recebimento</S.Title>
            <S.RedButton onClick={callNewReceivement} disabled={isDisabledButton}>
              Chamar Fornecedor!
            </S.RedButton>
            <SwitchMui
              style={{ marginTop: '14px' }}
              checked={currentDock?.is_fresh}
              offText='Mercearia'
              onText='Fresh'
              onClick={changeDockFresh}
              size='big'
            />
          </S.Card>
        </S.Wrapper>
        <ModalConfirmDockFresh
          show={showModalConfirmDockFresh}
          isFresh={currentDock?.is_fresh}
          onClose={() => setShowModalConfirmDockFresh(false)}
          onAction={(badge) => changeDockFresh(badge)}
        />
      </>
    )
  }

  return (
    <S.Wrapper>
      <S.Card>
        <S.Title>Recebimento {currentReceivement.id}</S.Title>
        <S.Form onSubmit={handleSubmit(beginReceivement)}>
          <S.WrapperVendor>
            <S.VendorLabel> Fornecedor:</S.VendorLabel>
            <S.VendorName>{currentReceivement.vendorName}</S.VendorName>
          </S.WrapperVendor>
          <Field
            labelText="Crachá"
            name="badge"
            control={control}
            autoComplete="off"
            type="password"
            onKeyDown={(e) => handleOnKeyDown(e, 'attendancePassword')}
          />
          <Field
            labelText="Senha de Atendimento"
            name="attendancePassword"
            control={control}
            autoComplete="off"
          />
          <S.SubmitButton type="submit" value="Iniciar Recebimento" />
        </S.Form>
      </S.Card>
    </S.Wrapper>
  )
}

export default NewReceivementStart
